import * as React from 'react'
import styled, { css } from 'styled-components'

import * as bp from '../../../../constants/breakpoints'
import * as colors from '../../../../constants/colors'
import * as card from '../../../../styles/card/reward'
import * as copy from '../../../../styles/copy'
import * as heading from '../../../../styles/heading'
import * as modal from '../../../../styles/modal'
import * as select from '../../../../styles/form/select'
import * as typography from '../../../../constants/neue/typography'

import * as accounting from '../../../../core/accounting'
import { isMobile } from '../../../../utils'
import * as text from '../../../../utils/text'

import { Button } from '../../../button/Button'
import { Modal } from '../../../Modal'
import { Option } from '../../../form/select/Option'
import { Select } from '../../../form/select/Select'
import { StatusPill } from '../../../tag/StatusPill'
import { Tag } from '../../../tag/Tag'

import { DefaultRewardImage } from '../DefaultRewardImage'
import * as type from '../type'

import { Card } from './RewardCard'
import { RewardCarousel } from './RewardCarousel'

type VariantModalType = type.RewardCardProps & type.RewardModalProps

export function OptionsModal({
  className,
  disabled,
  open,
  onClose,
  onSelectOptions,
  ...props
}: VariantModalType): React.ReactElement | null {
  const options = props.isMultiVariationsEnabled ? props.reward.options : props.reward.variants

  const shouldShowImagePlaceholderForLegacyProject =
    (!props.isMultiVariationsEnabled &&
      props.reward.variants?.some((variant) => {
        return Boolean(variant.imageURL)
      })) ||
    false

  const [selectedOptionIds, setSelectedOptionIds] = React.useState<Record<number | string, string | undefined>>({
    ...(props.selectedVariantId && { default: props.selectedVariantId }),
  })

  const [resolvedVariant, setResolvedVariant] = React.useState<type.Variant | undefined>(undefined)

  // NOTE: This and all its uses shall be removed once "multi-variations" is the only supported system.
  const selectedLegacyVariant = props.isMultiVariationsEnabled
    ? undefined
    : props.reward.variants?.find((option) => selectedOptionIds.default === option.id)

  const isSelectionComplete = React.useMemo(
    () => getIsSelectionComplete(selectedOptionIds),
    [options, props.isMultiVariationsEnabled, selectedOptionIds, selectedLegacyVariant],
  )

  const [carouselIndex, setCarouselIndex] = React.useState(0)

  if (!options || options.length === 0) {
    return null
  }

  const isRewardStockLimited = props.reward.stock.available != null

  const availableStock = props.isMultiVariationsEnabled
    ? resolvedVariant?.stock.total === null
      ? null
      : resolvedVariant?.stock.available
    : selectedLegacyVariant?.stock?.available

  // NOTE: from all option values, we only keep the ones with an image
  const allOptionValuesWithImage = (props.reward.options ?? [])
    ?.flatMap((option) => option.values)
    ?.filter((optionValue) => optionValue.imageURL !== undefined)

  return (
    <StyledModal
      data-testid="modal:option.pick"
      open={open}
      onClose={() => {
        onClose()
        setSelectedOptionIds({})
        setResolvedVariant(undefined)
        setCarouselIndex(0)
      }}
      closable
      className={className}
      lockScroll
      overlayScrollEnabled
    >
      <ModalContent>
        <StyledRewardCard
          onSelectOptions={onSelectOptions}
          {...props}
          mainImage={
            props.isMultiVariationsEnabled ? (
              resolvedVariant?.imageUrl ? (
                <card.CardImage src={resolvedVariant.imageUrl} alt={resolvedVariant.id} />
              ) : (
                <RewardCarousel
                  count={carouselIndex}
                  onCountChange={setCarouselIndex}
                  reward={props.reward}
                  locale={props.locale}
                  currency={props.currency}
                  isPrevDisabled={carouselIndex === 0}
                  isNextDisabled={
                    props.reward.img
                      ? carouselIndex === allOptionValuesWithImage.length
                      : carouselIndex === allOptionValuesWithImage.length - 1
                  }
                />
              )
            ) : undefined
          }
          hideCta
        />
        <List>
          <heading.M>{props.translations.variantModalTitle}</heading.M>
          {props.isMultiVariationsEnabled ? (
            props.reward.options?.map((option, optionIndex) => {
              const selectedValue = option.values.find((value) => selectedOptionIds[option.id] === value.id)
              const stock = selectedValue?.stock?.available

              const shouldShowImagePlaceholder = option.values.some((value) => {
                return Boolean(value.imageURL)
              })

              return (
                <React.Fragment key={option.id}>
                  <OptionHeading>
                    <OptionName>{option.name}</OptionName>
                    {option.description && <copy.S.regular>{option.description}</copy.S.regular>}
                  </OptionHeading>
                  {option && option.values.length >= 6 ? (
                    <OptionSelect
                      $index={options.length - optionIndex}
                      currency={props.currency}
                      isLegacyMode={false}
                      locale={props.locale}
                      optionValues={option.values}
                      selectedVariantId={selectedOptionIds[option.id]}
                      setSelectedVariantId={(selectedVariantId) => {
                        onSelectOption(option, selectedVariantId, optionIndex)
                      }}
                      shouldShowImagePlaceholder={shouldShowImagePlaceholder}
                      translations={props.translations}
                    />
                  ) : (
                    <OptionValues>
                      {option.values.map((value) => {
                        const isOptionValueStockAvailable = isStockAvailable(value)
                        return (
                          <OptionCard
                            data-testid="modal:option.pick.value.select"
                            $disabled={!isOptionValueStockAvailable}
                            $hasImage={Boolean(value.imageURL) || shouldShowImagePlaceholder}
                            key={value.id}
                            onClick={() => {
                              onSelectOption(option, value.id, optionIndex)
                            }}
                            $selected={selectedOptionIds[option.id] === value.id}
                          >
                            {value.imageURL ? (
                              <img alt={value.title} src={value.imageURL} />
                            ) : (
                              shouldShowImagePlaceholder && <DefaultRewardImage />
                            )}
                            <OptionRow>
                              <copy.S.regular>{value.title}</copy.S.regular>
                              {Boolean(value.additionalCost) && value.additionalCost! > 0 && (
                                <copy.S.regular>
                                  <Tag color="blue" size="small">
                                    +{'\u00A0'}
                                    {accounting.localeMoney(value.additionalCost!, {
                                      currency: props.currency,
                                      lang: props.locale,
                                    })}
                                  </Tag>
                                </copy.S.regular>
                              )}
                              {!isOptionValueStockAvailable && (
                                <copy.S.regular>
                                  <Tag color="sand" size="small">
                                    {props.translations.soldOut}
                                  </Tag>
                                </copy.S.regular>
                              )}
                            </OptionRow>
                          </OptionCard>
                        )
                      })}
                    </OptionValues>
                  )}
                  {!props.reward.isCombined &&
                    !isRewardStockLimited &&
                    props.translations.availableVariantStock &&
                    selectedOptionIds &&
                    stock && (
                      <StockPill bordered color="green" $squeezeBottomMargin={optionIndex === options.length - 1}>
                        {props.translations.availableVariantStock(stock)}
                      </StockPill>
                    )}
                </React.Fragment>
              )
            })
          ) : (
            <>
              {props.reward.variants && props.reward.variants.length >= 6 ? (
                <OptionSelect
                  $index={1}
                  currency={props.currency}
                  isLegacyMode={true}
                  locale={props.locale}
                  selectedVariantId={selectedLegacyVariant?.id}
                  setSelectedVariantId={(selectedVariantId) => {
                    setSelectedOptionIds({ default: selectedVariantId })
                  }}
                  shouldShowImagePlaceholder={shouldShowImagePlaceholderForLegacyProject}
                  translations={props.translations}
                  variants={props.reward.variants}
                />
              ) : (
                <OptionValues>
                  {props.reward.variants?.map((variant) => {
                    const isVariantStockAvailable = isStockAvailable(variant)
                    return (
                      <OptionCard
                        $disabled={!isVariantStockAvailable}
                        $hasImage={Boolean(variant.imageURL) || shouldShowImagePlaceholderForLegacyProject}
                        key={variant.id}
                        onClick={() => {
                          if (isVariantStockAvailable) {
                            setSelectedOptionIds({ default: variant.id })
                          }
                        }}
                        $selected={selectedOptionIds.default === variant.id}
                      >
                        {variant.imageURL ? (
                          <img alt={variant.title} src={variant.imageURL} />
                        ) : (
                          shouldShowImagePlaceholderForLegacyProject && <DefaultRewardImage />
                        )}
                        {variant.title}
                        {Boolean(variant.additionalCost) && variant.additionalCost! > 0 && (
                          <Tag color="blue" size="small">
                            +{'\u00A0'}
                            {accounting.localeMoney(variant.additionalCost!, {
                              currency: props.currency,
                              lang: props.locale,
                            })}
                          </Tag>
                        )}
                        {!isVariantStockAvailable && (
                          <Tag color="sand" size="small">
                            {props.translations.soldOut}
                          </Tag>
                        )}
                      </OptionCard>
                    )
                  })}
                </OptionValues>
              )}
            </>
          )}

          {isSelectionComplete &&
            !isRewardStockLimited &&
            ((props.isMultiVariationsEnabled && props.reward.isCombined) || !props.isMultiVariationsEnabled) && (
              <>
                {Boolean(availableStock) && props.translations.availableVariantStock && (
                  <StockPill bordered color="green" $squeezeBottomMargin>
                    {props.translations.availableVariantStock(availableStock ?? 0)}
                  </StockPill>
                )}
                {availableStock === 0 && props.translations.soldOut && (
                  <StockPill bordered color="red" $squeezeBottomMargin>
                    {props.translations.soldOut}
                  </StockPill>
                )}
              </>
            )}
          <ConfirmButton
            data-testid="modal:option.pick.confirm"
            disabled={!isSelectionComplete || disabled || availableStock === 0}
            onClick={() => {
              if (props.isMultiVariationsEnabled && resolvedVariant) {
                onClose(
                  props.reward.id,
                  Object.values(selectedOptionIds).filter((optionId) => optionId !== undefined) as string[],
                  resolvedVariant.id,
                )
                setResolvedVariant(undefined)
                setCarouselIndex(0)
              } else if (selectedLegacyVariant) {
                onClose(selectedLegacyVariant.id)
              }
              setSelectedOptionIds({})
            }}
          >
            {props.translations.variantModalCta}
          </ConfirmButton>
        </List>
      </ModalContent>
    </StyledModal>
  )

  function getIsSelectionComplete(selectedOptionIds: Record<string, string | undefined>): string | boolean | undefined {
    return props.isMultiVariationsEnabled
      ? options?.length === Object.keys(selectedOptionIds).length
      : selectedLegacyVariant?.id
  }

  async function handleCarouselScroll(
    clickedOptionIndex: number,
    clickedOptionValueId: string,
    selectedOptionIds: Record<string, string | undefined>,
  ): Promise<void> {
    if (props.reward.isCombined) {
      const deepestActiveOption = (props.reward.options ?? []).findLast((option) => option.id in selectedOptionIds)
      const deepestActiveOptionIndex = (props.reward.options ?? []).findIndex(
        (option) => option.id === deepestActiveOption?.id,
      )
      const deepestActiveOptionValue = deepestActiveOption?.values.find(
        (optionValue) => optionValue.id === selectedOptionIds[deepestActiveOption.id],
      )

      const indexOfClickedOptionValueInAllOptionValuesWithImage = allOptionValuesWithImage.findIndex(
        (optionValue) => optionValue.id === clickedOptionValueId,
      )

      if (indexOfClickedOptionValueInAllOptionValuesWithImage >= 0) {
        if (clickedOptionIndex >= deepestActiveOptionIndex) {
          setCarouselIndex(
            props.reward.img
              ? indexOfClickedOptionValueInAllOptionValuesWithImage + 1
              : indexOfClickedOptionValueInAllOptionValuesWithImage,
          )
        } else {
          if (deepestActiveOptionValue?.imageURL === undefined) {
            const lastOptionValueIndex = getIndexOfActiveCarouselImage(
              props.reward.options ?? [],
              allOptionValuesWithImage,
              selectedOptionIds,
              Boolean(props.reward.img),
            )

            if (lastOptionValueIndex) {
              setCarouselIndex(lastOptionValueIndex)
            } else {
              setCarouselIndex(0)
            }
          } else {
            const nextCarouselIndex = findCarouselIndex(allOptionValuesWithImage, deepestActiveOptionValue.id)

            setCarouselIndex(props.reward.img ? nextCarouselIndex + 1 : nextCarouselIndex)
          }
        }
      } else {
        if (clickedOptionIndex >= deepestActiveOptionIndex) {
          const lastOptionValueIndex = getIndexOfActiveCarouselImage(
            props.reward.options ?? [],
            allOptionValuesWithImage,
            selectedOptionIds,
            Boolean(props.reward.img),
          )

          if (lastOptionValueIndex >= 0) {
            setCarouselIndex(lastOptionValueIndex)
          }
        } else {
          if (deepestActiveOptionValue?.imageURL) {
            const nextCarouselIndex = findCarouselIndex(allOptionValuesWithImage, deepestActiveOptionValue.id)

            setCarouselIndex(props.reward.img ? nextCarouselIndex + 1 : nextCarouselIndex)
          } else {
            const lastOptionValueIndex = getIndexOfActiveCarouselImage(
              props.reward.options ?? [],
              allOptionValuesWithImage,
              selectedOptionIds,
              Boolean(props.reward.img),
            )

            if (lastOptionValueIndex) {
              setCarouselIndex(lastOptionValueIndex)
            } else {
              setCarouselIndex(0)
            }
          }
        }
      }
    } else {
      const nextCarouselIndex = findCarouselIndex(allOptionValuesWithImage, clickedOptionValueId)

      if (nextCarouselIndex >= 0) {
        setCarouselIndex(props.reward.img ? nextCarouselIndex + 1 : nextCarouselIndex)
      } else {
        setCarouselIndex(0)
      }
    }
  }

  async function onSelectOption(option: type.RewardOption, selectedVariantId: string, optionIndex: number) {
    const nextSelectedOptionIds = { ...selectedOptionIds, [option.id]: selectedVariantId }

    setSelectedOptionIds(nextSelectedOptionIds)

    const isSelectionComplete = getIsSelectionComplete(nextSelectedOptionIds)

    if (isSelectionComplete && !disabled) {
      const variant = await onSelectOptions(
        props.reward.id,
        Object.values(nextSelectedOptionIds).filter((value): value is string => value !== undefined),
      )
      setResolvedVariant(variant)

      if (variant.imageUrl) {
        return
      }
    }
    await handleCarouselScroll(optionIndex, selectedVariantId, nextSelectedOptionIds)
  }
}

function getIndexOfActiveCarouselImage(
  options: type.RewardOption[],
  allOptionValuesWithImage: type.RewardOption['values'],
  selectedOptionIds: Record<string, string | undefined>,
  hasRewardImage: boolean,
): number {
  const reversedRewardOptions = [...options].reverse()

  const indexOfLastActiveOptionValueWithImage = reversedRewardOptions.reduce((acc, option) => {
    if (isOptionActive(option)) {
      const selectedOptionId = selectedOptionIds[option.id]
      const activeOptionValue = option.values.find((optionValue) => {
        return optionValue.id === selectedOptionId
      })

      const hasOptionValueImage = Boolean(activeOptionValue?.imageURL)

      if (activeOptionValue && hasOptionValueImage && acc === -1) {
        acc = findCarouselIndex(allOptionValuesWithImage, activeOptionValue.id)
        return acc
      }

      return acc
    } else return acc
  }, -1)

  function isOptionActive(option: type.RewardOption): boolean {
    return option.id in selectedOptionIds
  }

  return hasRewardImage ? indexOfLastActiveOptionValueWithImage + 1 : indexOfLastActiveOptionValueWithImage
}

function findCarouselIndex(
  allOptionValuesWithImage: type.RewardOption['values'],
  clickedOptionValueId: string,
): number {
  return allOptionValuesWithImage.findIndex((optionValue) => optionValue.id === clickedOptionValueId)
}

type OptionSelectProps = {
  $index?: number
  className?: string
  currency: string
  locale: string
  selectedVariantId: string | undefined
  setSelectedVariantId: (id: string) => void
  shouldShowImagePlaceholder: boolean
  translations: VariantModalType['translations']
} & (
  | {
      isLegacyMode: false
      optionValues: type.RewardOption['values']
    }
  | {
      isLegacyMode: true
      variants: type.LegacyVariant[]
    }
)

function BaseOptionSelect(props: OptionSelectProps) {
  return (
    <Select
      className={props.className}
      filterable={props.isLegacyMode ? props.variants.length > 10 : props.optionValues.length > 10}
      height={400}
      label={(selected) => <OptionSelectLabel selected={selected} {...props} />}
      onChange={(selected) => {
        return props.setSelectedVariantId(selected[0].value)
      }}
      placeholder={props.translations.cta}
      selected={[props.selectedVariantId]}
    >
      {props.isLegacyMode
        ? props.variants?.map((variant) => (
            <Option disabled={!isStockAvailable(variant)} label={variant.title} key={variant.id} value={variant.id}>
              <LegacyVariantSelectDescription variant={variant} {...props} />
            </Option>
          ))
        : props.optionValues.map((optionValue) => (
            <Option
              disabled={!isStockAvailable(optionValue)}
              label={optionValue.title}
              key={optionValue.id}
              value={optionValue.id}
            >
              <OptionSelectDescription optionValue={optionValue} {...props} />
            </Option>
          ))}
    </Select>
  )
}

export const OptionSelect = styled(BaseOptionSelect)<{ $index: number }>`
  ${({ $index = 1 }) => {
    return css`
      ${select.Drawer} {
        position: relative;
        z-index: ${$index};
      }
    `
  }};
`

function OptionSelectDescription({
  optionValue,
  ...props
}: OptionSelectProps & {
  optionValue: type.RewardOption['values'][0]
}) {
  const isOptionStockAvailable = isStockAvailable(optionValue)
  return (
    <OptionSelectDescriptionWrapper>
      <Image>
        {optionValue.imageURL ? (
          <img alt={optionValue.title} src={optionValue.imageURL} />
        ) : (
          props.shouldShowImagePlaceholder && <DefaultRewardImage />
        )}
      </Image>

      <Title>{optionValue.title}</Title>

      {Boolean(optionValue.additionalCost) && (
        <Tag color={!isOptionStockAvailable ? 'sand' : 'blue'} size="small">
          +{'\u00A0'}
          {accounting.localeMoney(optionValue.additionalCost!, {
            currency: props.currency,
            lang: props.locale,
          })}
        </Tag>
      )}

      {!isOptionStockAvailable && (
        <Tag color="sand" size="small">
          {props.translations.soldOut}
        </Tag>
      )}
    </OptionSelectDescriptionWrapper>
  )
}

function LegacyVariantSelectDescription({
  variant,
  ...props
}: OptionSelectProps & {
  variant: type.LegacyVariant
}): React.ReactElement {
  return (
    <OptionSelectDescriptionWrapper>
      <Image>
        {variant.imageURL ? (
          <img alt={variant.title} src={variant.imageURL} />
        ) : (
          props.shouldShowImagePlaceholder && <DefaultRewardImage />
        )}
      </Image>

      <Title>{variant.title}</Title>

      {Boolean(variant.additionalCost) && (
        <Tag color={!isStockAvailable(variant) ? 'sand' : 'blue'} size="small">
          +{'\u00A0'}
          {accounting.localeMoney(variant.additionalCost!, {
            currency: props.currency,
            lang: props.locale,
          })}
        </Tag>
      )}

      {!isStockAvailable(variant) && (
        <Tag color="sand" size="small">
          {props.translations.soldOut}
        </Tag>
      )}
    </OptionSelectDescriptionWrapper>
  )
}

function OptionSelectLabel({
  selected,
  ...props
}: {
  selected: {
    label: string
  }[]
} & OptionSelectProps) {
  const selectedValue = props.isLegacyMode
    ? props.variants?.find((variant) => variant.id === props.selectedVariantId)
    : props.optionValues.find((optionValue) => optionValue.id === props.selectedVariantId)
  if (selectedValue) {
    const title = text.shorten(selectedValue.title, isMobile() ? 20 : 40)
    return (
      <Label>
        {props.isLegacyMode ? (
          <LegacyVariantSelectDescription {...props} variant={{ ...(selectedValue as type.LegacyVariant), title }} />
        ) : (
          <OptionSelectDescription
            {...props}
            optionValue={{ ...(selectedValue as type.RewardOption['values'][0]), title }}
          />
        )}
      </Label>
    )
  } else if (selected[0]?.label) {
    return selected[0]?.label
  }

  return props.translations.cta
}

function isStockAvailable(value: type.RewardOption['values'][0] | type.LegacyVariant) {
  const stockAvailable = value.stock.available
  return stockAvailable === null || stockAvailable === undefined || stockAvailable > 0
}

const Title = styled.span`
  margin-right: 4px;
  word-break: break-word;
`

const Label = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  ${Title} {
    margin-right: 0;
  }
`

const ConfirmButton = styled(Button)`
  margin-top: 20px;
`

const Image = styled.span`
  display: flex;
`

const OptionSelectDescriptionWrapper = styled.div`
  img,
  ${DefaultRewardImage} {
    margin-bottom: 3px;
  }

  color: inherit;
`

const ModalContent = styled.div`
  @media screen and ${bp.TABLET} {
    display: grid;
    grid-template-columns: 290px 1fr;
    gap: 16px;
  }
`

const OptionHeading = styled.div``

const OptionName = styled(copy.L.semiBold)``

const StyledRewardCard = styled(Card)`
  display: none;

  @media screen and ${bp.TABLET} {
    display: flex;
    height: fit-content;
  }
`

const OptionValues = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  list-style-type: none;
  margin: 0;
  padding: 0;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 16px;
`

const StockPill = styled(StatusPill)<{ $squeezeBottomMargin?: boolean; $squeezeTopMargin?: boolean }>`
  margin-bottom: ${({ $squeezeBottomMargin = false }) => ($squeezeBottomMargin ? '-20px' : '0')};
  margin-top: ${({ $squeezeTopMargin = false }) => ($squeezeTopMargin ? '-16px' : '0')};
`

const StyledModal = styled(Modal)`
  align-content: center;
  display: block;

  ${modal.Wrapper} {
    margin: auto;
    padding: 16px;
  }

  ${Select} {
    position: relative;
    max-width: 100%;

    & + ${StockPill} {
      margin-top: 0;
    }

    & + ${ConfirmButton} {
      margin-top: 0;
    }

    ${OptionSelectDescriptionWrapper} {
      align-items: center;
      display: grid;
      gap: 4px;
      grid-template-columns: max-content 5fr max-content max-content;
      grid-template-rows: 1fr;
      height: inherit;
      width: 100%;
      margin-right: 8px;

      img,
      ${DefaultRewardImage} {
        border-radius: 4px;
        width: 30px;
        height: 30px;
        min-width: 30px;
        margin-bottom: 0;
        object-fit: cover;
        margin-right: 8px;
      }
    }

    ${Label} {
      ${OptionSelectDescriptionWrapper} {
        grid-template-columns: max-content auto max-content;
      }
    }
  }

  @media screen and ${bp.TABLET} {
    ${modal.Wrapper} {
      display: flex;
      max-height: max-content;
      max-width: 85%;
      overflow: visible;
      padding: 24px 16px;
      width: fit-content;
    }

    ${Select} {
      ${Label} {
        ${OptionSelectDescriptionWrapper} {
          grid-template-columns: max-content 150px max-content;
        }
      }
    }
  }

  @media screen and (min-width: 900px) {
    ${Select} {
      ${OptionSelectDescriptionWrapper} {
        img,
        ${DefaultRewardImage} {
          height: 40px;
          min-width: 40px;
          width: 40px;
        }
      }

      ${Label} {
        ${OptionSelectDescriptionWrapper} {
          grid-template-columns: max-content auto max-content;
        }
      }
    }
  }

  @media screen and ${bp.LAPTOP} {
    ${modal.Wrapper} {
      max-width: 862px;
    }
  }
`

export const OptionCard = styled.li<{
  $disabled: boolean
  $hasImage: boolean
  $selected: boolean
  $hasHover?: boolean
}>`
  align-items: flex-start;
  border-radius: 4px;
  border: ${({ $selected }) => ($selected ? '2px' : '1px')} solid
    ${({ $selected }) => ($selected ? colors.PRIMARY_BLUE_500 : colors.BORDER_PRIMARY)};
  box-sizing: content-box;
  display: flex;
  gap: 8px;
  height: fit-content;
  margin: ${({ $selected }) => ($selected ? '-1px' : '0px')};
  padding: 8px;
  transition: border-color 0.2s ease-in-out;
  width: fit-content;

  ${({ $disabled = false, $hasHover = true }) => {
    if ($disabled) {
      return css`
        background-color: ${colors.BACKGROUND_DISABLED};
        border: 1px solid ${colors.BORDER_DISABLED};
        color: ${colors.TEXT_TERTIARY};
        cursor: not-allowed;
        pointer-events: none;
      `
    }

    if ($hasHover) {
      return css`
        &:hover {
          border: 2px solid ${colors.PRIMARY_BLUE_500};
          cursor: pointer;
          margin: -1px;
        }
      `
    } else {
      return css`
        &:hover {
          cursor: default;
        }
      `
    }
  }};

  ${({ $hasImage = false }) => {
    if ($hasImage) {
      return css`
        flex-direction: column;
        height: auto;
        width: 120px;

        figure,
        img {
          height: 120px;
          object-fit: cover;
          width: 120px;
        }
      `
    }
  }};
`

const OptionRow = styled.span`
  display: flex;
  flex-direction: row;
  flex-flow: wrap;
  gap: 8px;
  word-break: break-word;
`
